import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts';
import { EChartsOption } from 'echarts';
import { FileApiService } from '@angular-workspace/services';
import { FormatService } from '../../../shared/services/format.service';
import { parseFilePreviewPayload } from '../file-preview.helper';

export interface VersionFile {
  name: string;
  size?: string;
  isSelected?: boolean;
}

/** Raw file item from getFilesForPreview API */
interface ApiFileItem {
  key?: string;
  size?: number;
  isFile?: boolean;
  lastModified?: string;
}

/** Healthcare theme — matches var(--primary-color) */
const HC_PRIMARY = '#0b6c92';
const HC_CHART_COLORS = [
  '#0b6c92',
  '#00a8cc',
  '#3d8fb0',
  '#094d6b',
  '#2e3ba3',
  '#6b7280',
];

@Component({
  selector: 'app-dataset-detail-visualisation-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './visualisation-tab.component.html',
  styleUrl: './visualisation-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatasetDetailVisualisationTabComponent {
  readonly datasetId = input<string | null>(null);
  readonly license = input<string>('');

  chartType = 'bar';
  chartTypeLabel = 'Bar Chart';
  chartTypes: { key: string; label: string }[] = [
    { key: 'bar', label: 'Bar Chart' },
    { key: 'line', label: 'Line Chart' },
    { key: 'pie', label: 'Pie Chart' },
  ];
  metricTypes: string[] = ['sum', 'count', 'min', 'max', 'avg', 'std', 'var'];
  groupByColumns: string[] = [];
  selectedDimension = '';
  selectedSeries = '';
  selectedMetric = 'count';
  selectedColumnIsNumeric = false;
  selectedGroupByColumn = '';
  selectedFile: VersionFile | null = null;

  loading = false;
  loadingFiles = false;
  error = '';

  displayFiles: VersionFile[] = [];
  displayFilesTotalSize = '';
  displayFilesLastUpdated = '';

  tableHeaders: string[] = [];
  tableData: Record<string, unknown>[] = [];

  chartOptions: EChartsOption = {};

  fileMetadata: Record<string, unknown> | null = null;
  fileMetadataLoading = false;

  private fileApiService = inject(FileApiService);
  private cdr = inject(ChangeDetectorRef);
  private formatService = inject(FormatService);

  private loadGeneration = 0;

  constructor() {
    effect(() => {
      const id = this.datasetId();
      const gen = ++this.loadGeneration;
      untracked(() => {
        void this.onDatasetIdChange(id, gen);
      });
    });
  }

  private async onDatasetIdChange(id: string | null, gen: number): Promise<void> {
    this.selectedFile = null;
    this.displayFiles = [];
    this.displayFilesTotalSize = '';
    this.displayFilesLastUpdated = '';
    this.tableHeaders = [];
    this.tableData = [];
    this.chartOptions = {};
    this.fileMetadata = null;
    this.error = '';
    if (!id) {
      this.cdr.markForCheck();
      return;
    }
    await this.loadFilesFromApi(id, gen);
  }

  private async loadFilesFromApi(id: string, gen: number): Promise<void> {
    this.loadingFiles = true;
    this.error = '';
    this.cdr.markForCheck();

    try {
      const response = await this.fileApiService.getFilesForPreview(id);
      const apiFiles = this.extractFilesFromResponse(response?.data);
      if (gen !== this.loadGeneration) return;

      if (apiFiles.length > 0) {
        const filtered = apiFiles.filter((f) => f.isFile !== false);
        let totalSizeBytes = 0;
        let latestModified: Date | null = null;

        for (const f of filtered) {
          const size = f.size || 0;
          totalSizeBytes += size;
          if (f.lastModified) {
            const d = new Date(f.lastModified);
            if (!latestModified || d > latestModified) latestModified = d;
          }
        }

        this.displayFiles = filtered.map((f, i) => ({
          name: f.key || `file_${i + 1}`,
          size: (f.size || 0) > 0 ? this.formatFileSize(f.size || 0) : undefined,
          isSelected: false,
        }));

        this.displayFilesTotalSize =
          totalSizeBytes > 0 ? this.formatFileSize(totalSizeBytes) : '';
        this.displayFilesLastUpdated = latestModified
          ? this.formatService.formatDate(latestModified.toISOString())
          : '';
      } else {
        this.displayFiles = [];
        this.displayFilesTotalSize = '';
        this.displayFilesLastUpdated = '';
        this.fileMetadata = null;
      }
    } catch (err: unknown) {
      if (gen === this.loadGeneration) {
        this.error =
          'Failed to load files: ' +
          (err instanceof Error ? err.message : 'Unknown error');
        this.displayFiles = [];
        this.fileMetadata = null;
      }
      console.error('Error loading files for visualisation:', err);
    } finally {
      if (gen === this.loadGeneration) this.loadingFiles = false;
      this.cdr.markForCheck();
    }
  }

  /** API file shape from getFilesForPreview */
  private extractFilesFromResponse(data: unknown): ApiFileItem[] {
    if (!data || typeof data !== 'object') return [];
    const d = data as { files?: ApiFileItem[]; data?: { files?: ApiFileItem[] } };
    if (Array.isArray(d.files)) return d.files;
    const inner = d.data;
    if (
      inner &&
      typeof inner === 'object' &&
      Array.isArray((inner as { files?: ApiFileItem[] }).files)
    ) {
      return (inner as { files: ApiFileItem[] }).files;
    }
    return [];
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    );
  }

  onSelectedFileChange(file: VersionFile): void {
    this.selectedFile = file;
    this.loadPreview();
    this.loadFileMetadataForSelectedFile();
    this.scrollToTabView();
  }

  private scrollToTabView(): void {
    setTimeout(() => {
      const tabsEl = document.getElementById('dataset-detail-tabs');
      if (!tabsEl) return;
      const headerOffset = 100;
      const rect = tabsEl.getBoundingClientRect();
      const scrollTop =
        window.scrollY ?? document.documentElement.scrollTop;
      const targetY = scrollTop + rect.top - headerOffset;
      window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
    }, 0);
  }

  get selectedFileSizeDisplay(): string {
    if (this.fileMetadata && this.fileMetadata['size'] != null) {
      const raw = this.fileMetadata['size'];
      const bytes =
        typeof raw === 'string' ? parseInt(raw, 10) : (raw as number);
      return !isNaN(bytes) ? this.formatFileSize(bytes) : this.displayFilesTotalSize;
    }
    return this.displayFilesTotalSize;
  }

  get selectedFileLastUpdatedDisplay(): string {
    const lastMod = this.fileMetadata?.['lastModified'];
    if (lastMod && typeof lastMod === 'string') {
      return (
        this.formatService.formatDate(lastMod) || this.displayFilesLastUpdated
      );
    }
    return this.displayFilesLastUpdated;
  }

  async loadFileMetadataForSelectedFile(): Promise<void> {
    const dsId = this.datasetId();
    if (!this.selectedFile || !dsId) {
      this.fileMetadata = null;
      this.cdr.markForCheck();
      return;
    }
    this.fileMetadataLoading = true;
    this.fileMetadata = null;
    this.cdr.markForCheck();
    try {
      const res = await this.fileApiService.getFileMetadata(dsId, {
        key: this.selectedFile.name,
      });
      const raw = res?.data as Record<string, unknown> | undefined;
      const inner =
        raw?.['success'] && raw?.['data'] != null
          ? (raw['data'] as Record<string, unknown>)
          : raw;
      this.fileMetadata = inner ?? null;
    } catch {
      this.fileMetadata = null;
    } finally {
      this.fileMetadataLoading = false;
      this.cdr.markForCheck();
    }
  }

  async loadPreview(): Promise<void> {
    if (!this.selectedFile) {
      this.error = 'No file or dataset selected';
      this.cdr.markForCheck();
      return;
    }

    const dsId = this.datasetId();
    if (!dsId) {
      this.error = 'Dataset not available';
      this.cdr.markForCheck();
      return;
    }

    this.loading = true;
    this.error = '';

    try {
      const response = await this.fileApiService.generatePreview(
        dsId,
        this.selectedFile.name
      );
      this.processPreviewData(response?.data);
    } catch (err: unknown) {
      this.loading = false;
      this.error =
        'Failed to load preview: ' +
        (err instanceof Error ? err.message : 'Unknown error');
      this.cdr.markForCheck();
    }
  }

  private processPreviewData(previewData: unknown): void {
    this.loading = false;
    const parsed = parseFilePreviewPayload(previewData);
    if (parsed.error) {
      this.error = parsed.error;
      this.tableHeaders = [];
      this.tableData = [];
      this.cdr.markForCheck();
      return;
    }
    this.error = '';
    this.tableHeaders = parsed.headers;
    this.tableData = parsed.rows;

    if (this.tableHeaders.length > 0) {
      this.selectedDimension = this.tableHeaders[0];
      this.selectedColumnIsNumeric = this.isColumnNumeric(
        this.tableData,
        this.selectedDimension
      );
      if (this.selectedColumnIsNumeric) {
        this.metricTypes = ['sum', 'count', 'min', 'max', 'avg', 'std', 'var'];
        this.groupByColumns = this.getNonNumericColumns(this.tableData);
        this.selectedGroupByColumn = this.groupByColumns[0] || '';
      } else {
        this.metricTypes = ['count'];
        this.selectedMetric = 'count';
      }
      this.updateChartOptions();
    }

    this.cdr.markForCheck();
  }

  onChartTypeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.chartType = target.value;
    this.chartTypeLabel =
      this.chartTypes.find((c) => c.key === this.chartType)?.label ||
      'Bar Chart';
    if (this.chartTypeLabel === 'Line Chart') {
      const numericCols = this.getNumericColumnNames(this.tableData);
      this.selectedDimension =
        numericCols[0] || this.tableHeaders[0];
      this.selectedSeries = numericCols[1] || numericCols[0] || '';
    }
    this.updateChartOptions();
    this.cdr.markForCheck();
  }

  onDimensionChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedDimension = target.value;
    this.selectedColumnIsNumeric = this.isColumnNumeric(
      this.tableData,
      this.selectedDimension
    );
    if (this.selectedColumnIsNumeric) {
      this.metricTypes = ['sum', 'count', 'min', 'max', 'avg', 'std', 'var'];
      this.groupByColumns = this.getNonNumericColumns(this.tableData);
      this.selectedGroupByColumn = this.groupByColumns[0] || '';
    } else {
      this.metricTypes = ['count'];
      this.selectedMetric = 'count';
    }
    this.updateChartOptions();
    this.cdr.markForCheck();
  }

  onSeriesChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedSeries = target.value;
    this.updateChartOptions();
    this.cdr.markForCheck();
  }

  onMetricChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedMetric = target.value;
    this.updateChartOptions();
    this.cdr.markForCheck();
  }

  onGroupByColumnChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedGroupByColumn = target.value;
    this.updateChartOptions();
    this.cdr.markForCheck();
  }

  private getNumericColumnNames(
    data: Record<string, unknown>[]
  ): string[] {
    if (!Array.isArray(data) || data.length === 0) return [];
    const firstRow = data[0];
    return Object.keys(firstRow).filter((key) => {
      const value = firstRow[key];
      return (
        value !== '' &&
        !isNaN(Number(value)) &&
        isFinite(Number(value))
      );
    });
  }

  private getNonNumericColumns(
    data: Record<string, unknown>[]
  ): string[] {
    if (!Array.isArray(data) || data.length === 0) return [];
    return Object.keys(data[0]).filter(
      (col) =>
        !data.every((row) => {
          const value = row[col];
          return (
            value === '' ||
            (!isNaN(Number(value)) && isFinite(Number(value)))
          );
        })
    );
  }

  private isColumnNumeric(
    data: Record<string, unknown>[],
    columnName: string
  ): boolean {
    if (!Array.isArray(data) || data.length === 0) return false;
    return data.every((row) => {
      const value = row[columnName];
      return (
        value === '' ||
        (!isNaN(Number(value)) && isFinite(Number(value)))
      );
    });
  }

  private getDistinctValues<T>(data: T[], key: keyof T): unknown[] {
    return Array.from(new Set(data.map((item) => item[key])));
  }

  private getCountsByColumn<T>(
    data: T[],
    key: keyof T
  ): number[] {
    const counts: Record<string, number> = {};
    data.forEach((item) => {
      const val = item[key];
      if (val !== undefined) {
        counts[String(val)] = (counts[String(val)] || 0) + 1;
      }
    });
    return Object.entries(counts).map(([, count]) => count);
  }

  private groupBy<T>(
    data: T[],
    key: keyof T
  ): Record<string, T[]> {
    return data.reduce(
      (acc, row) => {
        const groupKey = String(row[key]);
        if (!acc[groupKey]) acc[groupKey] = [];
        acc[groupKey].push(row);
        return acc;
      },
      {} as Record<string, T[]>
    );
  }

  private getSumByColumn<T>(
    data: T[],
    groupByCol: keyof T,
    valueCol: keyof T
  ): number[] {
    const grouped = this.groupBy(data, groupByCol);
    return Object.values(grouped).map((rows) =>
      rows.reduce((sum, row) => sum + Number(row[valueCol]), 0)
    );
  }

  private getMinByColumn<T>(
    data: T[],
    groupByCol: keyof T,
    valueCol: keyof T
  ): number[] {
    const grouped = this.groupBy(data, groupByCol);
    return Object.values(grouped).map((rows) =>
      Math.min(...rows.map((row) => Number(row[valueCol])))
    );
  }

  private getMaxByColumn<T>(
    data: T[],
    groupByCol: keyof T,
    valueCol: keyof T
  ): number[] {
    const grouped = this.groupBy(data, groupByCol);
    return Object.values(grouped).map((rows) =>
      Math.max(...rows.map((row) => Number(row[valueCol])))
    );
  }

  private getAvgByColumn<T>(
    data: T[],
    groupByCol: keyof T,
    valueCol: keyof T
  ): number[] {
    const grouped = this.groupBy(data, groupByCol);
    return Object.values(grouped).map((rows) => {
      const nums = rows.map((row) => Number(row[valueCol]));
      return nums.reduce((sum, n) => sum + n, 0) / nums.length;
    });
  }

  private getXYPairs(
    data: Record<string, unknown>[],
    xCol: string,
    yCol: string
  ): [unknown, unknown][] {
    if (!Array.isArray(data) || !xCol || !yCol) return [];
    return data
      .filter(
        (row) =>
          Object.prototype.hasOwnProperty.call(row, xCol) &&
          Object.prototype.hasOwnProperty.call(row, yCol)
      )
      .map((row) => [row[xCol], row[yCol]]);
  }

  private getNameValuePairs(
    xAxis: unknown[],
    yAxis: number[]
  ): { value: number; name: string }[] {
    return xAxis.map((name, i) => ({
      value: yAxis[i],
      name: String(name),
    }));
  }

  private updateChartOptions(): void {
    if (!this.tableData?.length) return;
    this.tableHeaders = Object.keys(this.tableData[0] || {});

    if (this.chartTypeLabel === 'Bar Chart') {
      let xAxis: unknown[], yAxis: number[];
      const dim = this.selectedDimension as keyof (typeof this.tableData)[0];
      const groupBy = this.selectedGroupByColumn as keyof (typeof this.tableData)[0];

      switch (this.selectedMetric) {
        case 'sum':
          yAxis = this.getSumByColumn(this.tableData, groupBy, dim);
          xAxis = this.getDistinctValues(this.tableData, groupBy);
          break;
        case 'min':
          yAxis = this.getMinByColumn(this.tableData, groupBy, dim);
          xAxis = this.getDistinctValues(this.tableData, groupBy);
          break;
        case 'max':
          yAxis = this.getMaxByColumn(this.tableData, groupBy, dim);
          xAxis = this.getDistinctValues(this.tableData, groupBy);
          break;
        case 'avg':
          yAxis = this.getAvgByColumn(this.tableData, groupBy, dim);
          xAxis = this.getDistinctValues(this.tableData, groupBy);
          break;
        default:
          yAxis = this.getCountsByColumn(this.tableData, dim);
          xAxis = this.getDistinctValues(this.tableData, dim);
      }

      this.chartOptions = {
        tooltip: {},
        xAxis: {
          type: 'category',
          data: xAxis as string[],
          axisLabel: {
            rotate: (xAxis as string[]).length <= 13 ? 30 : 0,
            interval: (xAxis as string[]).length <= 13 ? 0 : 'auto',
          },
        },
        yAxis: { type: 'value' },
        series: [
          {
            name: 'Value',
            data: yAxis,
            type: 'bar',
            itemStyle: {
              color: HC_PRIMARY,
              borderRadius: [5, 5, 0, 0],
            },
          },
        ],
      };
    } else if (this.chartTypeLabel === 'Line Chart') {
      const numericCols = this.getNumericColumnNames(this.tableData);
      this.selectedDimension =
        this.selectedDimension || numericCols[0];
      this.selectedSeries =
        this.selectedSeries || numericCols[1] || numericCols[0];
      const chartData = this.getXYPairs(
        this.tableData,
        this.selectedDimension,
        this.selectedSeries
      ).map(
        ([x, y]) =>
          [x ?? null, y ?? null] as [
            string | number | null,
            string | number | null,
          ]
      );

      this.chartOptions = {
        xAxis: { type: 'category' },
        yAxis: { type: 'value' },
        series: [
          {
            symbolSize: 12,
            data: chartData,
            type: 'line',
            smooth: true,
            itemStyle: { color: HC_PRIMARY },
            lineStyle: { color: HC_PRIMARY },
          },
        ],
      };
    } else if (this.chartTypeLabel === 'Pie Chart') {
      let xAxis: unknown[], yAxis: number[];
      const dim = this.selectedDimension as keyof (typeof this.tableData)[0];
      const groupBy = this.selectedGroupByColumn as keyof (typeof this.tableData)[0];

      switch (this.selectedMetric) {
        case 'sum':
          yAxis = this.getSumByColumn(this.tableData, groupBy, dim);
          xAxis = this.getDistinctValues(this.tableData, groupBy);
          break;
        case 'min':
        case 'max':
        case 'avg':
          yAxis = this.getAvgByColumn(this.tableData, groupBy, dim);
          xAxis = this.getDistinctValues(this.tableData, groupBy);
          break;
        default:
          yAxis = this.getCountsByColumn(this.tableData, dim);
          xAxis = this.getDistinctValues(this.tableData, dim);
      }
      const pieChartData = this.getNameValuePairs(xAxis, yAxis);

      this.chartOptions = {
        tooltip: { trigger: 'item' },
        legend: { show: false },
        series: [
          {
            name: this.selectedDimension,
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            label: { show: false },
            labelLine: { show: false },
            data: pieChartData,
            itemStyle: {
              color: (params: { dataIndex: number }) =>
                HC_CHART_COLORS[
                params.dataIndex % HC_CHART_COLORS.length
                ],
            },
          },
        ],
      };
    }
  }

  readonly supportedFormats = ['csv', 'tsv', 'xlsx', 'parquet', 'json'];

  isFileSupportedForVisualisation(file: VersionFile): boolean {
    const ext = (file.name || '').split('.').pop()?.toLowerCase() || '';
    return this.supportedFormats.includes(ext);
  }
}
